import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Calendar, CheckCircle2, Clock, AlertTriangle, Flag,
  Mail, MessageSquare, X, Save
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: {
    label: 'Pending',
    icon: Clock,
    color: 'bg-gray-100 text-gray-700 border-gray-200',
  },
  awaiting_response: {
    label: 'Awaiting Response',
    icon: Mail,
    color: 'bg-blue-100 text-blue-700 border-blue-200',
  },
  resolved: {
    label: 'Resolved',
    icon: CheckCircle2,
    color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  },
  escalated: {
    label: 'Escalated',
    icon: AlertTriangle,
    color: 'bg-red-100 text-red-700 border-red-200',
  },
  no_action_needed: {
    label: 'No Action Needed',
    icon: CheckCircle2,
    color: 'bg-gray-100 text-gray-600 border-gray-200',
  },
};

export default function ActionPlan({ audit, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email_sent: audit?.action_plan?.email_sent || false,
    deadline_days: audit?.action_plan?.deadline_days || 7,
    notes: audit?.action_plan?.notes || '',
  });

  const actionPlan = audit?.action_plan;
  const hasActionPlan = actionPlan && actionPlan.status;

  const handleSaveAction = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/audits/${audit._id}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await res.json();
      
      if (!res.ok) throw new Error(data.detail || 'Failed to save action plan');

      toast.success('Action plan saved!');
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.message || 'Failed to save action plan');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/audits/${audit._id}/action`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });

      const data = await res.json();
      
      if (!res.ok) throw new Error(data.detail || 'Failed to update status');

      toast.success(`Marked as ${STATUS_CONFIG[newStatus].label}`);
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.message || 'Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const getDeadlineInfo = () => {
    if (!actionPlan?.follow_up_deadline) return null;
    
    const deadline = new Date(actionPlan.follow_up_deadline);
    const now = new Date();
    const daysLeft = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
    
    const isOverdue = daysLeft < 0;
    const isUrgent = daysLeft <= 2 && daysLeft >= 0;
    
    return { deadline, daysLeft, isOverdue, isUrgent };
  };

  const deadlineInfo = getDeadlineInfo();

  // Show compact view if no action plan set yet
  if (!hasActionPlan && !isEditing) {
    return (
      <Card className="border-dashed border-2" data-testid="action-plan-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-sm flex items-center gap-2">
                <Flag className="w-4 h-4 text-primary" />
                Action Plan
              </CardTitle>
              <CardDescription className="text-xs mt-1">
                Track follow-up actions and team accountability
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(true)}
              className="gap-1.5"
            >
              <Calendar className="w-3.5 h-3.5" />
              Set Action Plan
            </Button>
          </div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card data-testid="action-plan-card">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-sm flex items-center gap-2">
              <Flag className="w-4 h-4 text-primary" />
              Action Plan
            </CardTitle>
            {hasActionPlan && !isEditing && (
              <div className="flex items-center gap-2 mt-2">
                <Badge
                  className={`${STATUS_CONFIG[actionPlan.status]?.color} border`}
                  data-testid={`action-status-${actionPlan.status}`}
                >
                  {React.createElement(STATUS_CONFIG[actionPlan.status]?.icon, {
                    className: 'w-3 h-3 mr-1',
                  })}
                  {STATUS_CONFIG[actionPlan.status]?.label}
                </Badge>
                {deadlineInfo && (
                  <Badge
                    variant="outline"
                    className={
                      deadlineInfo.isOverdue
                        ? 'bg-red-50 text-red-700 border-red-200'
                        : deadlineInfo.isUrgent
                        ? 'bg-amber-50 text-amber-700 border-amber-200'
                        : 'bg-blue-50 text-blue-700 border-blue-200'
                    }
                  >
                    <Clock className="w-3 h-3 mr-1" />
                    {deadlineInfo.isOverdue
                      ? `${Math.abs(deadlineInfo.daysLeft)}d overdue`
                      : `${deadlineInfo.daysLeft}d left`}
                  </Badge>
                )}
              </div>
            )}
          </div>
          {hasActionPlan && !isEditing && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsEditing(true)}
              className="h-7"
            >
              Edit
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent>
        <AnimatePresence mode="wait">
          {isEditing ? (
            <motion.div
              key="edit"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-4"
            >
              {/* Email Sent Checkbox */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="email_sent"
                  checked={formData.email_sent}
                  onChange={(e) =>
                    setFormData({ ...formData, email_sent: e.target.checked })
                  }
                  className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <label htmlFor="email_sent" className="text-sm font-medium">
                  ✉️ Email sent to team member
                </label>
              </div>

              {/* Deadline Days */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">
                  Follow-up Deadline (days)
                </label>
                <Input
                  type="number"
                  min="1"
                  max="60"
                  value={formData.deadline_days}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      deadline_days: parseInt(e.target.value) || 7,
                    })
                  }
                  className="h-9"
                />
                <p className="text-xs text-muted-foreground">
                  Team member should respond within{' '}
                  <strong>{formData.deadline_days} days</strong>
                </p>
              </div>

              {/* Notes */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                  <MessageSquare className="w-3 h-3" />
                  Notes (optional)
                </label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  placeholder="e.g., Discussed with John - he'll fix high WOS items by Friday"
                  rows={3}
                  className="text-sm"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 pt-2">
                <Button
                  onClick={handleSaveAction}
                  disabled={loading}
                  size="sm"
                  className="gap-1.5"
                >
                  <Save className="w-3.5 h-3.5" />
                  {loading ? 'Saving...' : 'Save Action Plan'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(false)}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="view"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-3"
            >
              {/* Status Info */}
              {actionPlan?.email_sent && (
                <div className="flex items-start gap-2 text-sm">
                  <Mail className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-foreground">
                      Email sent to team
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(actionPlan.sent_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}

              {deadlineInfo && (
                <div className="flex items-start gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-foreground">
                      Deadline: {deadlineInfo.deadline.toLocaleDateString()}
                    </p>
                    <p
                      className={`text-xs ${
                        deadlineInfo.isOverdue
                          ? 'text-red-600 font-semibold'
                          : deadlineInfo.isUrgent
                          ? 'text-amber-600 font-semibold'
                          : 'text-muted-foreground'
                      }`}
                    >
                      {deadlineInfo.isOverdue
                        ? `⚠️ ${Math.abs(deadlineInfo.daysLeft)} days overdue`
                        : deadlineInfo.isUrgent
                        ? `⏰ ${deadlineInfo.daysLeft} days left (urgent)`
                        : `${deadlineInfo.daysLeft} days remaining`}
                    </p>
                  </div>
                </div>
              )}

              {actionPlan?.notes && (
                <div className="flex items-start gap-2 text-sm">
                  <MessageSquare className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="bg-secondary/50 rounded-lg p-2.5 flex-1">
                    <p className="text-xs text-foreground whitespace-pre-wrap">
                      {actionPlan.notes}
                    </p>
                  </div>
                </div>
              )}

              {actionPlan?.status === 'awaiting_response' && (
                <>
                  <Separator />
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-xs text-muted-foreground">Quick Actions:</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusUpdate('resolved')}
                      disabled={loading}
                      className="h-7 gap-1 text-xs"
                    >
                      <CheckCircle2 className="w-3 h-3" />
                      Mark Resolved
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusUpdate('escalated')}
                      disabled={loading}
                      className="h-7 gap-1 text-xs text-red-600 hover:text-red-700"
                    >
                      <AlertTriangle className="w-3 h-3" />
                      Escalate
                    </Button>
                  </div>
                </>
              )}

              {actionPlan?.resolved_at && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5">
                  <p className="text-xs text-emerald-700 flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    <strong>Resolved</strong> on{' '}
                    {new Date(actionPlan.resolved_at).toLocaleDateString()}
                  </p>
                </div>
              )}

              {actionPlan?.escalated_at && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-2.5">
                  <p className="text-xs text-red-700 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    <strong>Escalated</strong> on{' '}
                    {new Date(actionPlan.escalated_at).toLocaleDateString()}
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}
